import { call } from '@advanza/api'
import { FileDropZone, InputField } from '@advanza/input'
import { Button, Divider, LoadingDots } from '@advanza/ui'
import type { CodeBlockEditorDescriptor } from '@mdxeditor/editor'
import Card from 'components/Card'
import AutoRow from 'components/misc/AutoRow'
import { useAppSelector } from 'hooks/hooks'
import { useEffect, useState } from 'react'
import style from './uploadUserTaggingRules.module.css'

const UploadUserTaggingRules = () => {
    const { countries } = useAppSelector((state) => state.countries)
    const [url, setUrl] = useState<string | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(false)
    const [result, setResult] = useState('')
    const [error, setError] = useState(false)
    const [requestIds, setRequestIds] = useState('')

    useEffect(() => {
        if (url === undefined) {
            call(`office/misc/get-user-tagging-rules-url/${countries[0]}`).then(
                ({ url }) => setUrl(url),
                () => setUrl('FORBIDDEN'),
            )
        }
    }, [url])

    const [md, setMd] = useState('')
    const [mdxEditorModule, setMdxEditorModule] = useState<
        typeof import('@mdxeditor/editor') | null
    >(null)

    useEffect(() => {
        call('office/misc/get-user-tagging-rules-doc').then(
            ({ md }) => setMd(md),
            () => setMd('FORBIDDEN'),
        )
    }, [])

    useEffect(() => {
        import('@mdxeditor/editor').then((module) => setMdxEditorModule(module))
    }, [])

    if (!mdxEditorModule) {
        return null
    }

    const PlainTextCodeEditorDescriptor: CodeBlockEditorDescriptor = {
        // always use the editor, no matter the language or the meta of the code block
        match: (_language, _meta) => true,
        // You can have multiple editors with different priorities, so that there's a "catch-all" editor (with the lowest priority)
        priority: 0,
        // The Editor is a React component
        Editor: (props) => {
            // stops the propagation so that the parent lexical editor does not handle certain events.
            return (
                <pre>
                    <code>{props.code}</code>
                </pre>
            )
        },
    }

    const { codeBlockPlugin, headingsPlugin, linkPlugin, listsPlugin, MDXEditor } = mdxEditorModule
    const plugins = [
        codeBlockPlugin({ codeBlockEditorDescriptors: [PlainTextCodeEditorDescriptor] }),
        headingsPlugin(),
        linkPlugin(),
        listsPlugin(),
    ]

    const uploadUserTaggingRules = async (file: File, isTest: boolean, addComments: boolean) => {
        try {
            setIsLoading(true)
            setResult('')
            setError(false)
            const response = await call('office/misc/upload-user-tagging-rules', {
                payload: {
                    file,
                    isTest,
                    addComments,
                    requestIds,
                    countryCode: countries[0],
                },
            })
            setResult(addComments ? response.newJson : JSON.stringify(response, null, 4))

            if (!isTest && !addComments) {
                setUrl(undefined)
            }
        } catch (errorResponse: any) {
            setResult(errorResponse.error || errorResponse)
            setError(true)
        }

        setIsLoading(false)
    }

    const createDropZone = (isTest: boolean, addComments: boolean) => (
        <FileDropZone
            mime="application/json"
            multiple={false}
            maxFiles={1}
            onFilesAccepted={(files: File[]) =>
                uploadUserTaggingRules(files[0], isTest, addComments)
            }
            onFilesRejected={() => {}}>
            <Button disabled={isLoading}>
                {isLoading ? (
                    <LoadingDots />
                ) : isTest ? (
                    '🔍 Validate rules / test against request ids'
                ) : addComments ? (
                    '📝 Update json comments'
                ) : (
                    '⚠️ Replace current rules'
                )}
            </Button>
        </FileDropZone>
    )

    return (
        <>
            {url === undefined ? (
                <LoadingDots />
            ) : url ? (
                <a className="underline" href={url} target="_blank" rel="noreferrer">
                    Current rules
                </a>
            ) : (
                'No current rules'
            )}
            <Divider m />
            <AutoRow>
                {createDropZone(true, false)}
                {createDropZone(false, true)}
                {createDropZone(false, false)}
            </AutoRow>
            <Divider m />
            <InputField
                placeholder="Test request ids (comma separated) e.g.: 123, 456, 789"
                value={requestIds}
                onChange={(e) => setRequestIds(e.target.value)}
                disabled={isLoading}
            />
            <Divider s />
            <small style={{ color: '#aaa' }}>
                Due to testing in dry-run mode, using multiple request ids of the same user doesn't
                consider previous changes.
            </small>
            {result && (
                <>
                    <Divider m />
                    <pre style={{ whiteSpace: 'pre-wrap', color: error ? 'red' : 'initial' }}>
                        {result}
                    </pre>
                </>
            )}
            <Divider m />
            <Card useToggle header="Manual">
                <MDXEditor className={style.editor} markdown={md} plugins={plugins} readOnly />
            </Card>
        </>
    )
}

export default UploadUserTaggingRules
